import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"

import type { VoiceEngine } from "@/features/chat/utils/voice-engine"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import styles from "@/features/chat/styles/answer.module.css"
import { grantVoiceInternalDataConsent } from "@/features/chat/utils/voice-mode-consent"
import { cn } from "@/lib/utils"

type VoiceModeInfoModalProps = {
  open: boolean
  onClose: () => void
  onAccept: () => void
  /** Names the provider in the consent copy. Defaults to the Realtime wording. */
  engine?: VoiceEngine | null
}

export function VoiceModeInfoModal({ open, onClose, onAccept, engine }: VoiceModeInfoModalProps) {
  const { t } = useTranslation()

  const handleAccept = () => {
    grantVoiceInternalDataConsent()
    onAccept()
    onClose()
  }

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {t("voice_mode.info.title", "Voice Mode and data processing")}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <AlertDialogDescription asChild>
            <div className={cn(styles.answerText, "prose dark:prose-invert")}>
              <ReactMarkdown>
                {engine === "live"
                  ? t("voice_mode.info.description_live")
                  : t("voice_mode.info.description")}
              </ReactMarkdown>
            </div>
          </AlertDialogDescription>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={handleAccept}>
            {t("voice_mode.info.accept", "Continue to Voice Mode")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
